import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import './VariableMappingDialog.css';

/**
 * VariableMappingDialog
 * Allows users to map template variables to student attendance fields
 * 
 * @param {boolean} visible - Dialog visibility
 * @param {function} onHide - Callback when dialog is closed
 * @param {object} template - Template object with _id, name, content, templateType
 * @param {array} variables - Array of variables extracted from template
 * @param {array} availableFields - Array of available fields to map to
 * @param {object} existingMappings - Existing variable mappings (if editing)
 * @param {function} onSave - Callback when mappings are saved
 */
const VariableMappingDialog = ({
    visible,
    onHide,
    template,
    variables = [],
    availableFields = [],
    existingMappings = {},
    onSave,
}) => {
    const [mappings, setMappings] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [saving, setSaving] = useState(false);

    const validateMappings = useCallback((currentMappings) => {
        // Check if all variables are mapped
        const allMapped = variables.every(variable => currentMappings[variable]);
        setIsValid(allMapped && variables.length > 0);
    }, [variables]);

    // Add console log to debug
    useEffect(() => {
        console.log('[VariableMappingDialog] Props received:', {
            visible,
            template,
            variables,
            availableFields,
            existingMappings
        });
    }, [visible, template, variables, availableFields, existingMappings]);

    // Initialize mappings when dialog becomes visible or data changes
    useEffect(() => {
        if (!visible) {
            // Reset state when dialog is hidden
            setMappings({});
            setIsValid(false);
            return;
        }
        
        // Initialize mappings from existing or empty
        const initialMappings = { ...existingMappings };
        setMappings(initialMappings);
        validateMappings(initialMappings);
    }, [visible, existingMappings, validateMappings]);

    const handleMappingChange = (variable, fieldKey) => {
        const newMappings = {
            ...mappings,
            [variable]: fieldKey,
        };
        setMappings(newMappings);
        validateMappings(newMappings);
    };

    const handleSave = async () => {
        if (!isValid) {
            return;
        }

        setSaving(true);
        try {
            await onSave(mappings);
            onHide();
        } catch (error) {
            console.error('Error saving mappings:', error);
        } finally {
            setSaving(false);
        }
    };

    const getFieldsByCategory = () => {
        const categories = {};
        // Safeguard: ensure availableFields is an array
        const fields = Array.isArray(availableFields) ? availableFields : [];

        fields.forEach(field => {
            const category = field.category || 'Other';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(field);
        });
        return categories;
    };

    const fieldsByCategory = getFieldsByCategory();

    const getFieldLabel = (field) => {
        return field.computed
            ? `${field.label} (Auto-computed)`
            : field.label;
    };

    const renderVariableRow = (variable) => {
        // Safeguard: ensure availableFields is an array
        const fields = Array.isArray(availableFields) ? availableFields : [];
        const selectedField = fields.find(f => f.key === mappings[variable]);
        const isMapped = !!mappings[variable];

        // Group fields by category for dropdown
        const groupedFields = {};
        fields.forEach(field => {
            const category = field.category || 'Other';
            if (!groupedFields[category]) {
                groupedFields[category] = [];
            }
            groupedFields[category].push(field);
        });

        // Convert to array format for PrimeReact grouped dropdown
        const dropdownOptions = Object.keys(groupedFields).map(category => ({
            label: category,
            items: groupedFields[category]
        }));

        return (
            <div key={variable} className="variable-mapping-row">
                <div className="variable-info">
                    <div className="variable-label">
                        <Tag value={`{{${variable}}}`} severity="info" />
                    </div>
                    {selectedField && (
                        <div className="variable-example">
                            <small>Example: {selectedField.example}</small>
                        </div>
                    )}
                </div>
                <div className="variable-mapping">
                    <Dropdown
                        key={`${variable}-${visible}-${mappings[variable] || 'none'}`}
                        value={mappings[variable] || null}
                        options={dropdownOptions}
                        optionLabel={(field) => getFieldLabel(field)}
                        optionValue="key"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        onChange={(e) => handleMappingChange(variable, e.value)}
                        placeholder="Select field to map"
                        filter
                        showClear={isMapped}
                        className="mapping-dropdown"
                    />
                </div>
                <div className="variable-status">
                    {isMapped ? (
                        <i className="pi pi-check-circle" style={{ color: 'green' }} />
                    ) : (
                        <i className="pi pi-exclamation-circle" style={{ color: 'orange' }} />
                    )}
                </div>
            </div>
        );
    };

    const renderTemplatePreview = () => {
        if (!template?.content) return null;

        return (
            <div className="template-preview-section">
                <h4>Template Preview</h4>
                <div className="template-content-box">
                    {typeof template.content === 'string' ? (
                        <pre>{template.content}</pre>
                    ) : (
                        <>
                            <div><strong>Subject:</strong> {template.content.subject}</div>
                            <div><strong>Body:</strong> {template.content.body}</div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const dialogFooter = (
        <div className="mapping-dialog-footer">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
                disabled={saving}
            />
            <Button
                label={saving ? 'Saving...' : 'Save Mappings'}
                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                onClick={handleSave}
                disabled={!isValid || saving}
                className="p-button-primary"
            />
        </div>
    );

    const getMappedCount = () => {
        return Object.keys(mappings).filter(key => mappings[key]).length;
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header="Map Template Variables"
            footer={dialogFooter}
            style={{ width: '800px', maxWidth: '90vw' }}
            modal
            closable={!saving}
            className="variable-mapping-dialog"
        >
            <div className="mapping-dialog-content">
                <div className="mapping-info-banner">
                    <i className="pi pi-info-circle" />
                    <div>
                        <p>
                            Map template variables to ensure personalized notifications are sent with accurate data.
                        </p>
                    </div>
                </div>

                {template && (
                    <div className="template-info">
                        <strong>Template:</strong> {template.name}
                        <Tag value={template.templateType?.toUpperCase()} severity="info" style={{ marginLeft: '0.5rem' }} />
                    </div>
                )}

                {renderTemplatePreview()}

                {variables.length === 0 ? (
                    <Message
                        severity="warn"
                        text="No variables found in this template. You can use this template without mapping."
                    />
                ) : !Array.isArray(availableFields) || availableFields.length === 0 ? (
                    <Message
                        severity="error"
                        text="No field mappings available. Please contact support or try refreshing the page."
                    />
                ) : (
                    <>
                        <div className="mapping-progress">
                            <span>Mapped: {getMappedCount()} / {variables.length}</span>
                            {isValid && (
                                <Tag value="All variables mapped" severity="success" icon="pi pi-check" />
                            )}
                        </div>

                        <div className="variables-list">
                            {variables.map(variable => renderVariableRow(variable))}
                        </div>

                        {!isValid && variables.length > 0 && (
                            <Message
                                severity="error"
                                text="Please map all variables before saving."
                                style={{ marginTop: '1rem' }}
                            />
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
};

export default VariableMappingDialog;
